import React, { useState, useEffect, useCallback } from 'react';
import './SeatMap.scss';

interface Seat {
  id: number;
  row: string;
  column: number;
  seat_number: string;
  status: 'available' | 'selected' | 'reserved' | 'sold' | 'held';
  is_available: boolean;
  sold: boolean;
  disabled: boolean;
  held_until?: string;
  held_by?: string;
  in_cart: boolean;
}

interface SeatMapProps {
  seats: Seat[];
  selectedSeats: Set<number>;
  maxSeats: number;
  onSeatToggle: (seatId: number) => void;
  loading?: boolean;
  pendingSeats?: Set<number>;
  quotaSeatId?: number;
  conflictSeatId?: number;
}

const SeatMap: React.FC<SeatMapProps> = ({
  seats,
  selectedSeats,
  maxSeats,
  onSeatToggle,
  loading = false,
  pendingSeats,
  quotaSeatId,
  conflictSeatId
}) => {
  const [seatsByRow, setSeatsByRow] = useState<Record<string, Seat[]>>({});
  const [showLeftIndicator, setShowLeftIndicator] = useState(false);
  const [showRightIndicator, setShowRightIndicator] = useState(false);
  const [animatingSeats, setAnimatingSeats] = useState<Set<number>>(new Set());
  const [errorSeats, setErrorSeats] = useState<Set<number>>(new Set());

  // Group seats by row
  useEffect(() => {
    const grouped: Record<string, Seat[]> = {};
    seats.forEach(seat => {
      if (!grouped[seat.row]) {
        grouped[seat.row] = [];
      }
      grouped[seat.row].push(seat);
    });
    setSeatsByRow(grouped);
  }, [seats]);

  // Handle scroll indicators for mobile
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const scrollLeft = target.scrollLeft;
    const scrollWidth = target.scrollWidth;
    const clientWidth = target.clientWidth;
    
    setShowLeftIndicator(scrollLeft > 10);
    setShowRightIndicator(scrollLeft < scrollWidth - clientWidth - 10);
  }, []);

  // Check scroll indicators on mount and resize
  useEffect(() => {
    const checkScrollIndicators = () => {
      const seatGrid = document.querySelector('.seat-grid') as HTMLDivElement;
      if (seatGrid) {
        const scrollLeft = seatGrid.scrollLeft;
        const scrollWidth = seatGrid.scrollWidth;
        const clientWidth = seatGrid.clientWidth;
        
        setShowLeftIndicator(scrollLeft > 10);
        setShowRightIndicator(scrollLeft < scrollWidth - clientWidth - 10);
      }
    };

    checkScrollIndicators();
    window.addEventListener('resize', checkScrollIndicators);
    
    return () => {
      window.removeEventListener('resize', checkScrollIndicators);
    };
  }, [seatsByRow]);

  const getSeatStatus = (seat: Seat): string => {
    // If seat is in pending state (being released), show as available
    if (pendingSeats?.has(seat.id)) {
      return 'available';
    }
    
    // If seat is selected by current user
    if (selectedSeats.has(seat.id)) {
      return 'selected';
    }
    
    // If seat is in cart or held by someone else
    if (seat.in_cart) {
      return 'reserved';
    }
    if (seat.held_by && seat.held_until && new Date(seat.held_until) > new Date()) {
      return 'reserved';
    }
    
    // If seat is sold
    if (seat.sold) {
      return 'sold';
    }
    
    // If seat is disabled
    if (seat.disabled) {
      return 'disabled';
    }
    
    // Default to seat status
    return seat.status;
  };

  const isSeatClickable = (seat: Seat): boolean => {
    const status = getSeatStatus(seat);
    return status === 'available' || (status === 'selected' && selectedSeats.has(seat.id));
  };

  const handleSeatClick = useCallback((seat: Seat) => {
    if (isSeatClickable(seat)) {
      // Show loading state
      setAnimatingSeats(prev => new Set([...Array.from(prev), seat.id]));
      
      // Clear any previous error for this seat
      setErrorSeats(prev => {
        const newSet = new Set(prev);
        newSet.delete(seat.id);
        return newSet;
      });
      
      // Call the parent handler
      onSeatToggle(seat.id);
      
      // Remove loading state after delay for better visual feedback
      setTimeout(() => {
        setAnimatingSeats(prev => {
          const newSet = new Set(prev);
          newSet.delete(seat.id);
          return newSet;
        });
      }, 500);
    } else {
      // Show error animation for unavailable seats
      setErrorSeats(prev => new Set([...Array.from(prev), seat.id]));
      setTimeout(() => {
        setErrorSeats(prev => {
          const newSet = new Set(prev);
          newSet.delete(seat.id);
          return newSet;
        });
      }, 1000);
    }
  }, [onSeatToggle]);

  if (loading) {
    return (
      <div className="seat-map-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading seat map...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="seat-map">
      {/* Stage indicator */}
      <div className="stage-indicator">
        <div className="stage-platform">
          <i className="fas fa-arrow-up"></i>
          <span>Stage</span>
          <i className="fas fa-arrow-up"></i>
        </div>
      </div>

      {/* Mobile scroll indicators */}
      {showLeftIndicator && (
        <div className="scroll-indicator scroll-indicator-left">
          <i className="fas fa-chevron-left"></i>
        </div>
      )}
      {showRightIndicator && (
        <div className="scroll-indicator scroll-indicator-right">
          <i className="fas fa-chevron-right"></i>
        </div>
      )}

      {/* Seat grid */}
      <div 
        className="seat-grid" 
        role="grid" 
        aria-label="Seat selection grid"
        onScroll={handleScroll}
      >
        {Object.entries(seatsByRow).map(([row, rowSeats]) => (
          <div key={row} className="seat-row" role="row">
            {/* Seats in row */}
            {rowSeats.map(seat => {
              const status = getSeatStatus(seat);
              const isClickable = isSeatClickable(seat);
              
              const isAnimating = animatingSeats.has(seat.id);
              const isPending = pendingSeats?.has(seat.id) ?? false;
              const hasError = errorSeats.has(seat.id) || conflictSeatId === seat.id;
              const quotaHit = quotaSeatId === seat.id;
              const wasSelected = selectedSeats.has(seat.id);
              
              return (
                <button
                  key={seat.id}
                  type="button"
                  className={`seat ${status} ${isAnimating ? 'loading' : ''} ${isPending ? 'pending' : ''} ${hasError ? 'error' : ''} ${quotaHit ? 'quota' : ''} ${isPending && wasSelected ? 'releasing' : ''}`}
                  onClick={() => handleSeatClick(seat)}
                  disabled={!isClickable || isAnimating}
                  role="gridcell"
                  aria-label={`Seat ${seat.seat_number} - ${status.charAt(0).toUpperCase() + status.slice(1)}`}
                  title={`Seat ${seat.seat_number} - ${status.charAt(0).toUpperCase() + status.slice(1)}`}
                >
                  {isAnimating ? (
                    <div className="seat-loading">
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Selecting seat...</span>
                      </div>
                    </div>
                  ) : (
                    seat.seat_number
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SeatMap;
